import { MiniFooterComponent } from '@accounts/ui-common';
import { AccredibleButtonLoadingComponentModule } from '@accredible-frontend-v2/components/button-loading';
import { AccredibleDividerModule } from '@accredible-frontend-v2/components/divider';
import { AccredibleTextInputComponentModule } from '@accredible-frontend-v2/forms/text-input';
import { AccredibleBaseFormComponent } from '@accredible-frontend-v2/forms/utils';
import { emailValidator } from '@accredible-frontend-v2/forms/validators';
import { NavigationBackComponent } from '@accredible-frontend-v2/new-components/navigation-back';
import { TopBorderCardComponent } from '@accredible-frontend-v2/new-components/top-border-card';
import { TwoColumnsLayoutComponent } from '@accredible-frontend-v2/new-components/two-columns-layout';
import {
  AccredibleRecaptchaModule,
  AccredibleRecaptchaService,
} from '@accredible-frontend-v2/recaptcha';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { AccredibleToastService } from '@accredible-frontend-v2/toast';
import { Component, ElementRef, inject, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { RecaptchaComponent } from 'ng-recaptcha';
import { finalize } from 'rxjs/operators';
import { CtaPanelComponent } from '../cta-panel/cta-panel.component';
import { EmailSentComponentModule } from '../email-sent/email-sent.component.module';
import { ForgotPasswordApiService } from './forgot-password.service';

@Component({
  templateUrl: './forgot-password.container.html',
  styleUrls: [`./forgot-password.container.scss`],
  standalone: true,
  imports: [
    CtaPanelComponent,
    TwoColumnsLayoutComponent,
    TopBorderCardComponent,
    NavigationBackComponent,
    MiniFooterComponent,
    TranslocoModule,
    ReactiveFormsModule,
    EmailSentComponentModule,
    AccredibleTextInputComponentModule,
    AccredibleButtonLoadingComponentModule,
    AccredibleRecaptchaModule,
    AccredibleDividerModule,
    RouterModule,
  ],
})
export class ForgotPasswordContainer extends AccredibleBaseFormComponent<{
  email: string;
}> {
  protected readonly _el = inject(ElementRef<HTMLElement>);

  private readonly _forgotPasswordApi = inject(ForgotPasswordApiService);
  private readonly _fb = inject(UntypedFormBuilder);
  private readonly _language = inject(AccredibleLanguageService);
  private readonly _recaptcha = inject(AccredibleRecaptchaService);
  private readonly _toast = inject(AccredibleToastService);
  private readonly _title = inject(Title);

  isEmailSent = signal(false);
  isLoading = signal(false);

  private _recaptchaComponent: RecaptchaComponent;

  constructor() {
    super(inject(ElementRef<HTMLElement>));

    this.formGroup = this._fb.group({
      email: [null, { validators: [Validators.required, emailValidator()], updateOn: 'submit' }],
    });

    this._title.setTitle('Accredible • Accounts • Forgot Password');
  }

  recaptchaSubmit(): void {
    this._recaptcha.submit('forgotPassword', this._submit.bind(this));
  }

  onActionV2(recaptchaComponent: RecaptchaComponent): void {
    this._recaptchaComponent = recaptchaComponent;
  }

  private _submit(token: string, v3Recaptcha: boolean): void {
    if (this._isFormValid()) {
      this.isLoading.set(true);
      const email = this.formGroup.get('email').value;

      this._forgotPasswordApi
        .forgotPassword(email, token, v3Recaptcha)
        .pipe(
          finalize(() => {
            this.isLoading.set(false);
          }),
        )
        .subscribe({
          next: () => {
            this.isEmailSent.set(true);
          },
          error: () => {
            this._toast.error(this._language.translate('forgot-password.email_error'));
          },
        });
    }
  }
}
